import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { formatPrice } from '@helpers/formatPrice';
import { isExternallyManagedOrganization } from '@helpers/isExternallyManagedOrganization';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import PencilSvg from '@components/icons/Pencil.svg';
import PlusBalanceSvg from '@components/icons/PlusBalance.svg';
import { CellContent } from '@pages/Admin/BillingAccountsPage/components/Content/styles/CellContent';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useState } from 'react';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, FeatureFlagsService, OrganizationsService } from 'app/API';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { createFetchOrganizationQueryFn } from '../../api/fetchOrganization';
import { createFetchOrganizationFeatureFlagsQueryFn } from '../../api/fetchOrganizationFeatureFlags';
import { FeatureFlagsSection } from './components/FeatureFlagsSection';
import { NameSectionViewOnly } from './components/NameSection/NameSectionViewOnly';
import { RepositoriesTable } from './components/RepositoriesTable';
import { TeamsList } from './components/TeamsList';
import { WorkspacesTable } from './components/WorkspacesTable';
import { StyledLink } from './styles/StyledLink';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const { me } = useAuthContext();
  const isVsBilling = me?.roles?.includes('ROLE_BILLING_ADMIN');
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { id } = useParams<{ id: string }>();
  const organizationId = id ?? '';

  const organizationQuery = useQuery({
    queryKey: organizationsKeys.detail(organizationId),
    queryFn: createFetchOrganizationQueryFn(call),
    enabled: organizationId !== '',
  });
  const organization = organizationQuery.data ?? null;
  const canEditRates = isVsBilling && !isExternallyManagedOrganization(organization?.managementSource);

  const organizationSlug = organization?.slug ?? '';
  const featureFlagsQuery = useQuery({
    queryKey: organizationsKeys.featureFlagsByOrg(organizationId, organizationSlug),
    queryFn: createFetchOrganizationFeatureFlagsQueryFn(call),
    enabled: organizationId !== '' && organizationSlug !== '',
  });

  const invalidateFeatureFlags = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: organizationsKeys.featureFlagsByOrg(organizationId, organizationSlug),
      }),
      queryClient.invalidateQueries({ queryKey: organizationsKeys.lists() }),
    ]);

  const setFeatureFlagMutation = useMutation({
    mutationFn: ({ name, value }: { name: string; value: boolean }) =>
      call(() =>
        FeatureFlagsService.updateOrganizationFeatureFlag({ oid: organizationId, requestBody: { name, value } }),
      ),
    onError: () => toast.error('Failed to update the feature flag'),
    onSettled: invalidateFeatureFlags,
  });

  const removeFeatureFlagMutation = useMutation({
    mutationFn: (name: string) =>
      call(() => OrganizationsService.adminRemoveOrgFeatureFlag({ oid: organizationId, ffname: name })),
    onError: () => toast.error('Failed to remove the feature flag override'),
    onSettled: invalidateFeatureFlags,
  });

  const recalculateMutation = useMutation({
    mutationFn: (billingAccountId: string) => call(() => AdminService.recalculateBalances({ baId: billingAccountId })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: organizationsKeys.detail(organizationId) }),
  });

  const isSavingFeatureFlags = setFeatureFlagMutation.isPending || removeFeatureFlagMutation.isPending;
  const isLoading =
    organizationQuery.isFetching ||
    featureFlagsQuery.isFetching ||
    recalculateMutation.isPending ||
    isSavingFeatureFlags;

  const reloadPage = () => {
    closeModal();
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.detail(organizationId) });
  };

  const openBalanceModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminAddBalanceToBillingAccount, { billingAccountId, onSuccess: reloadPage });
  };

  const openRateModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminBillingAccountRate, { billingAccountId, onSuccess: reloadPage });
  };

  const openRatesModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminBillingAccountRatesEditor, { billingAccountId, onSuccess: reloadPage });
  };

  const recalculateBalances = (billingAccountId: string) => {
    recalculateMutation.mutate(billingAccountId);
  };

  const [baDisclosureOpen, setBADisclosureOpen] = useState<boolean>(true);

  const billingAccounts = organization?.BillingAccount
    ? [{ ...organization?.BillingAccount, billingConfiguration: organization.billingConfiguration }]
    : [];

  return (
    <>
      <LinearProgressLoader active={isLoading} />
      {organization ? (
        <Wrapper>
          <NameSectionViewOnly organization={organization} />
          <WorkspacesTable workspaces={organization.workspaces} />
          <RepositoriesTable repositories={organization.repositories} />
          <TeamsList teams={organization.Teams} />
          <FeatureFlagsSection
            organizationFlags={featureFlagsQuery.data?.organizationFlags ?? {}}
            defaultFlags={featureFlagsQuery.data?.defaultFlags ?? {}}
            isLoading={featureFlagsQuery.isLoading}
            isError={featureFlagsQuery.isError}
            isSaving={isSavingFeatureFlags}
            onSetFlag={(name, value) => setFeatureFlagMutation.mutate({ name, value })}
            onRemoveFlag={(name) => removeFeatureFlagMutation.mutate(name)}
            onRetry={() => void featureFlagsQuery.refetch()}
          />
          {isVsBilling ? (
            <DisclosureSection
              title={'Billing Accounts'}
              open={baDisclosureOpen}
              setOpenState={setBADisclosureOpen}
              loading={false}
            >
              <TableWrapper>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Billing Account Name</Th>
                      <Th>Balance</Th>
                      <Th>Tier Rate</Th>
                      <Th>Post Rate</Th>
                      <Th>Real-Time Rate</Th>
                      <Th>Partial Unlock Rate</Th>
                      <Th>Full Unlock Rate</Th>
                      <Th>Enhance on Demand Rate</Th>
                      <Th>Recalculate Balance</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {billingAccounts.map((data) => (
                      <Tr key={data.id}>
                        <Td>
                          <CellContent>
                            <StyledLink
                              to={routes.adminBillingAccountDetails.make(data?.id)}
                              className="font-medium text-[var(--color-title)] hover:text-foreground-muted"
                            >
                              {data.name}
                            </StyledLink>
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            <div className="flex flex-col">
                              <div className="flex flex-row items-center">
                                {formatCentsPrice(
                                  (data.MainBalance?.currentBalance ?? 0) +
                                    (data.FreeCreditBalance?.currentBalance ?? 0),
                                )}
                                {isVsBilling ? (
                                  <IconButtonClean
                                    type="button"
                                    title="Add goodwill credit"
                                    className="ml-[10px]"
                                    onClick={() => openBalanceModal(data.id)}
                                  >
                                    <PlusBalanceSvg />
                                  </IconButtonClean>
                                ) : null}
                              </div>
                              <div className="flex flex-row">
                                <small className="mr-2">
                                  Main: {formatCentsPrice(data.MainBalance?.currentBalance ?? 0)}
                                </small>
                                <small>
                                  Free Credit: {formatCentsPrice(data.FreeCreditBalance?.currentBalance ?? 0)}
                                </small>
                              </div>
                            </div>
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.fixedRate ? 'Fixed: ' : ''}
                            {formatPrice(data.tierRate ?? 0)}/hr
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rate"
                                className="ml-[10px]"
                                onClick={() => openRateModal(data.id)}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.billingConfiguration?.postProcessingRateType === 'DISALLOWED' ? 'Disallowed' : null}
                            {data.billingConfiguration?.postProcessingRateType === 'TIER_RATE' ? 'Tier Rate' : null}
                            {!['DISALLOWED', 'TIER_RATE'].includes(
                              data.billingConfiguration?.postProcessingRateType ?? '',
                            )
                              ? formatPrice(Number(data.billingConfiguration?.postProcessingRate ?? 0))
                              : null}
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rates"
                                className="ml-[10px]"
                                onClick={() => openRatesModal(data?.id ?? '')}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.billingConfiguration?.realTimeRateType === 'DISALLOWED' ? 'Disallowed' : null}
                            {data.billingConfiguration?.realTimeRateType === 'TIER_RATE' ? 'Tier Rate' : null}
                            {!['DISALLOWED', 'TIER_RATE'].includes(data.billingConfiguration?.realTimeRateType ?? '')
                              ? formatPrice(Number(data.billingConfiguration?.realTimeRate ?? 0))
                              : null}
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rates"
                                className="ml-[10px]"
                                onClick={() => openRatesModal(data?.id ?? '')}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.billingConfiguration?.partialUnlockRateType === 'DISALLOWED' ? 'Disallowed' : null}
                            {data.billingConfiguration?.partialUnlockRateType === 'TIER_RATE' ? 'Tier Rate' : null}
                            {!['DISALLOWED', 'TIER_RATE'].includes(
                              data.billingConfiguration?.partialUnlockRateType ?? '',
                            )
                              ? formatPrice(Number(data.billingConfiguration?.partialUnlockRate ?? 0))
                              : null}
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rates"
                                className="ml-[10px]"
                                onClick={() => openRatesModal(data.id ?? '')}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.billingConfiguration?.fullUnlockRateType === 'DISALLOWED' ? 'Disallowed' : null}
                            {data.billingConfiguration?.fullUnlockRateType === 'TIER_RATE' ? 'Tier Rate' : null}
                            {!['DISALLOWED', 'TIER_RATE'].includes(data.billingConfiguration?.fullUnlockRateType ?? '')
                              ? formatPrice(Number(data.billingConfiguration?.fullUnlockRate ?? 0))
                              : null}
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rates"
                                className="ml-[10px]"
                                onClick={() => openRatesModal(data.id ?? '')}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            {data.billingConfiguration?.enhancedOnDemandRateType === 'DISALLOWED' ? 'Disallowed' : null}
                            {data.billingConfiguration?.enhancedOnDemandRateType === 'TIER_RATE' ? 'Tier Rate' : null}
                            {!['DISALLOWED', 'TIER_RATE'].includes(
                              data.billingConfiguration?.enhancedOnDemandRateType ?? '',
                            )
                              ? formatPrice(Number(data.billingConfiguration?.enhancedOnDemandRate ?? 0))
                              : null}
                            {canEditRates ? (
                              <IconButtonClean
                                type="button"
                                title="Edit rates"
                                className="ml-[10px]"
                                onClick={() => openRatesModal(data.id ?? '')}
                              >
                                <PencilSvg />
                              </IconButtonClean>
                            ) : null}
                          </CellContent>
                        </Td>
                        <Td>
                          <CellContent>
                            <OutlinedButton onClick={() => recalculateBalances(data.id)}>Recalculate</OutlinedButton>
                          </CellContent>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableWrapper>
            </DisclosureSection>
          ) : null}
        </Wrapper>
      ) : null}
    </>
  );
};
